import { Flex, Text } from '@radix-ui/themes';
import { useMemo } from 'react';
import { IconHomeFilled } from '../icons/icons';
import { IconCaretRightFilled } from '@tabler/icons-react';
import { Link } from '@trashlab/sitesapp/src/organizations/best-deal/components/shared/link';
import { useRouter } from '@tanstack/react-router';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';
import { useThemeTextColor } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/use-theme-text-color';
import { BreadcrumbSchema } from '@trashlab/sitesapp/src/organizations/best-deal/components/seo/schemas/breadcrumb-schema';

export const Breadcrumbs = ({
  title,
  light,
  withinRentals,
}: {
  title: string;
  light?: boolean;
  withinRentals?: boolean;
}) => {
  const router = useRouter();
  const currentPath = router.state.location.pathname;
  const { isLightBackground } = useThemeTextColor();

  // Use provided light prop, or fallback to theme detection
  const isLight = light !== undefined ? light : isLightBackground;

  const textColor = isLight ? 'text-blue-950' : 'text-white';
  const hoverColor = isLight ? 'hover:text-blue-800' : 'hover:text-gray-200';

  // Build breadcrumb items for structured data
  // Uses window.location.origin for absolute URLs required by schema.org
  const breadcrumbItems = useMemo(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const items = [{ name: 'Home', url: `${baseUrl}/` }];

    if (withinRentals) {
      items.push({ name: 'Services', url: `${baseUrl}/dumpster-rentals` });
    }

    if (currentPath !== '/') {
      items.push({ name: title, url: `${baseUrl}${currentPath}` });
    }

    return items;
  }, [currentPath, title, withinRentals]);

  return (
    <>
      {/* BreadcrumbList structured data for SEO - enhances search result display */}
      <BreadcrumbSchema items={breadcrumbItems} />
      <Flex direction="row" gap="2" align="center" justify="start" mb="4">
        <Link href="/" className={cn(textColor, hoverColor)}>
          <IconHomeFilled color="currentColor" className="w-5 h-5" />
        </Link>
        {withinRentals && (
          <>
            <IconCaretRightFilled className={cn(textColor, 'w-3 h-3')} />
            <Link
              href="/dumpster-rentals"
              className={cn(textColor, 'flex items-center')}
            >
              <Text size="2" className={cn(textColor, hoverColor)}>
                Services
              </Text>
            </Link>
          </>
        )}
        {currentPath !== '/' && (
          <>
            <IconCaretRightFilled className={cn(textColor, 'w-3 h-3')} />
            <Text size="2" className={cn(textColor, hoverColor)}>
              {title}
            </Text>
          </>
        )}
      </Flex>
    </>
  );
};

export default Breadcrumbs;
